import { Injectable, InjectByToken } from '@jkhy/frontend-utils/di';
import { exists } from '@jkhy/frontend-utils/functions';
import {
    DynamicComponentExitReason,
    DynamicComponentHandle,
    DynamicComponentRegistry,
    DynamicComponentResult,
} from '@jkhy/frontend-utils/lit/dynamic-component-registry';
import { ObservationSource } from '@jkhy/frontend-utils/observable';
import { html, ReactiveElement, type TemplateResult } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import type { TmSideDrawer } from './side-drawer.component';
import {
    dispatchDrawerResult,
    DRAWER_CONTENT_CONTAINER_ID,
    DrawerConfirmationPromptToken,
    SideDrawerOptions,
    type SideDrawerPromptFn,
} from './side-drawer.service.types';

// eslint-disable-next-line no-duplicate-imports
import './side-drawer.component';

const DEFAULT_OPTIONS = Object.freeze<SideDrawerOptions>({
    host: createDrawerHost(),
    align: 'right',
    buttons: [],
    footerAlignment: 'left',
    promptOnClose: true,
    closeOnBlur: true,
});

function createDrawerHost(): HTMLElement {
    const host = document.createElement('div');
    host.style.display = 'contents';
    document.body.appendChild(host);
    return host;
}

type DrawerContent = string | TemplateResult;

@Injectable()
export class TmSideDrawerService {
    constructor(
        private readonly registry: DynamicComponentRegistry<TmSideDrawer>,
        /** Opens the confirmation dialog. Injected via token so legacy callers without DI can omit it. */
        @InjectByToken(DrawerConfirmationPromptToken)
        private readonly showConfirmation?: SideDrawerPromptFn
    ) {}

    public get drawerOpen() {
        return exists(this.handle);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handle?: DynamicComponentHandle<TmSideDrawer, any>;

    private defaultOptions?: Partial<SideDrawerOptions>;

    private readonly contentSource = new ObservationSource<Element>();

    private readonly sideDrawerRef = createRef<TmSideDrawer>();

    public setDefaultOptions(options?: Partial<SideDrawerOptions>) {
        this.defaultOptions = options;
    }

    public async open<T>(
        content: DrawerContent | ((setData: (data: T) => void) => DrawerContent),
        title: string,
        options: Partial<SideDrawerOptions> = {}
    ) {
        const allOptions = this.normalizeOptions(options);

        if (this.drawerOpen) {
            // Block if the open drawer requires confirmation — lets blur-close own the flow.
            if (this.handle && this.sideDrawerRef.value?.onBeforeClose) {
                return this.handle.disposed;
            }
            const result = await this.handleOpenDrawer(allOptions.promptOnClose);
            if (result) {
                return result;
            }
        }

        content =
            typeof content === 'function' ? content(data => this.dispatchData(data)) : content;
        const handle = this.registry.create<T>(() => {
            return this.renderDrawer(title, content, allOptions);
        }, allOptions);

        this.handle = handle;

        return handle.disposed;
    }

    public async close<T>(reason = DynamicComponentExitReason.ForceClose, data?: T) {
        if (!this.handle) {
            return;
        }

        const { handle } = this;

        this.handle = undefined;

        // Still dispose when disconnected so removeFromMap() runs and clears the element queue.
        // Swallow the error — disposePart() throws when the node is no longer in the DOM.
        if (!this.sideDrawerRef.value?.isConnected) {
            handle.dispose({ reason, data });
            handle.disposed.catch(() => {});
            return;
        }

        handle.dispose({ reason, data });
        await handle.disposed;
    }

    public async getDrawer() {
        if (!this.handle) {
            throw new Error('Could not get side drawer element. No drawer is open.');
        }

        const { element } = this.handle;
        await element.updateComplete;
        return element;
    }

    public getContent<E extends Element = Element>() {
        return this.contentSource.toPromise(true) as Promise<E>;
    }

    private async handleOpenDrawer(promptOnClose: boolean) {
        if (!this.handle) {
            throw new Error('No drawer is currently open');
        }

        const { handle } = this;

        if (promptOnClose) {
            const drawer = await this.getDrawer();
            if (!drawer.onBeforeClose) {
                throw new Error(
                    'Could not show drawer confirmation prompt. It was not provided via injection token.'
                );
            }

            drawer.isHandlingClose = true;
            const confirmed = await drawer.onBeforeClose();
            drawer.isHandlingClose = false;

            if (!confirmed) {
                return handle.disposed;
            }
        }

        return this.close();
    }

    private async contentChanged(content?: Element) {
        if (!content) {
            return;
        }

        if (this.sideDrawerRef.value) {
            await this.sideDrawerRef.value.updateComplete;
        }

        const { firstElementChild } = content;
        if (!firstElementChild) {
            throw new Error('Could not determine content element');
        }

        if (firstElementChild instanceof ReactiveElement) {
            await firstElementChild.updateComplete;
        }

        this.contentSource.emit(firstElementChild);
    }

    private dispatchData<T>(data: T) {
        this.getContent().then(contentElement => dispatchDrawerResult(contentElement, data));
    }

    private normalizeOptions(options: Partial<SideDrawerOptions>): SideDrawerOptions {
        return {
            ...DEFAULT_OPTIONS,
            ...this.defaultOptions,
            ...options,
        };
    }

    private renderDrawer<T>(
        title: string,
        content: string | TemplateResult,
        options: SideDrawerOptions
    ) {
        const { align, buttons, footerAlignment, closeOnBlur, promptOnClose, width } = options;
        const alignment = align === 'left' ? 'left: 0' : 'right: 0';
        const widthVar = width
            ? ` --jh-platform-drawer-width: ${width}; --jh-platform-panel-content-padding: 0;`
            : '';
        const style = `position: absolute; ${alignment}; top: 0; z-index: 1050;${widthVar}`;
        const contentTemplate = typeof content === 'string' ? html`<p>${content}</p>` : content;
        const { showConfirmation } = this;
        const onBeforeClose =
            promptOnClose && showConfirmation
                ? async () => {
                      const confirmContent = html`<p style="width: 300px">
                          Are you sure you want to close
                          <strong>${title}</strong>? You'll lose any unsaved work.
                      </p>`;
                      const { reason } = await showConfirmation(confirmContent, `Close ${title}?`);
                      return reason === DynamicComponentExitReason.Confirm;
                  }
                : undefined;

        return html`<tm-side-drawer
            ${ref(this.sideDrawerRef)}
            style=${style}
            .alignment=${align}
            .heading=${title}
            .buttons=${buttons}
            .footerAlignment=${footerAlignment}
            .closeOnBlur=${closeOnBlur}
            .onBeforeClose=${onBeforeClose}
            @close-drawer=${(e: CustomEvent<DynamicComponentResult<T>>) => {
                e.preventDefault();
                const { reason, data } = e.detail || {};
                this.close(reason, data);
            }}
        >
            <div
                id="${DRAWER_CONTENT_CONTAINER_ID}"
                style="display: contents;"
                ${ref(content => this.contentChanged(content))}
            >
                ${contentTemplate}
            </div>
        </tm-side-drawer>`;
    }
}
